import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Zap, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AbonnementenPage() {
  const { api, user, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Refresh user data on page load to get latest subscription status
  useEffect(function() {
    async function refreshUser() {
      try {
        const userRes = await api.get('/auth/me');
        updateUser(userRes.data);
      } catch (err) {
        console.error('Failed to refresh user data');
      }
    }
    refreshUser();
  }, []);

  useEffect(function() {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    if (sessionId) {
      checkPayment(sessionId);
    } else if (success === 'true') {
      // Payment Link redirect - refresh user data
      toast.success(t('subscriptions.paymentSuccess'));
      refreshUserData();
      window.history.replaceState({}, '', '/abonnementen');
    }
  }, [searchParams]);

  async function refreshUserData() {
    try {
      const userRes = await api.get('/auth/me');
      updateUser(userRes.data);
    } catch (err) {
      console.error('Failed to refresh user');
    }
  }

  async function checkPayment(sessionId) {
    setCheckingPayment(true);
    let attempts = 0;
    
    async function poll() {
      try {
        const res = await api.get('/stripe/status/' + sessionId);
        if (res.data.payment_status === 'paid') {
          toast.success(t('subscriptions.paymentSuccess'));
          const userRes = await api.get('/auth/me');
          updateUser(userRes.data);
          setCheckingPayment(false);
          window.history.replaceState({}, '', '/abonnementen');
        } else if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setCheckingPayment(false);
        }
      } catch (err) {
        setCheckingPayment(false);
      }
    }
    poll();
  }

  async function handleSubscribe(planId) {
    if (planId === 'free' || planId === user?.subscription_plan) return;
    setLoading(true);
    try {
      const res = await api.post('/stripe/checkout', {
        plan: planId,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
      setLoading(false);
    }
  }

  const currentPlan = user?.subscription_plan || 'free';

  return (
    <div className="space-y-6" data-testid="abonnementen-page">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground">{t('subscriptions.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('subscriptions.subtitle')}</p>
      </div>

      {checkingPayment && (
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
          <span className="text-blue-700 dark:text-blue-300">{t('subscriptions.verifyingPayment')}</span>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {/* Free Plan */}
        <Card className={'border-0 shadow-sm relative ' + (currentPlan === 'free' ? 'ring-2 ring-cyan-500' : '')} data-testid="plan-free">
          {currentPlan === 'free' && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-cyan-500 text-white">{t('subscriptions.current')}</Badge>
            </div>
          )}
          <CardHeader className="text-center pb-4">
            <div className="w-12 h-12 mx-auto bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center mb-4">
              <Star className="w-6 h-6 text-slate-600 dark:text-slate-400" />
            </div>
            <CardTitle className="text-xl font-heading">{t('subscriptions.free')}</CardTitle>
            <CardDescription>{t('subscriptions.freeDescription')}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold">€0</span>
              <span className="text-muted-foreground">/{t('subscriptions.month')}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3">
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">5 {i18n.language === 'en' ? 'time entries/month' : 'urenregistraties/maand'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">5 {i18n.language === 'en' ? 'mileage entries/month' : 'km registraties/maand'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">1 {i18n.language === 'en' ? 'client' : 'klant'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">1 {i18n.language === 'en' ? 'invoice/month' : 'factuur/maand'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">PDF {i18n.language === 'en' ? 'export' : 'export'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">KM Calculator</span></li>
              <li className="flex items-start gap-2 text-muted-foreground"><span className="w-5 h-5 flex items-center justify-center flex-shrink-0">✗</span><span className="text-sm">{i18n.language === 'en' ? 'No projects' : 'Geen projecten'}</span></li>
              <li className="flex items-start gap-2 text-muted-foreground"><span className="w-5 h-5 flex items-center justify-center flex-shrink-0">✗</span><span className="text-sm">{i18n.language === 'en' ? 'No email sending' : 'Geen e-mail versturen'}</span></li>
              <li className="flex items-start gap-2 text-muted-foreground"><span className="w-5 h-5 flex items-center justify-center flex-shrink-0">✗</span><span className="text-sm">{i18n.language === 'en' ? 'No dark mode' : 'Geen donkere modus'}</span></li>
            </ul>
            <Button className={'w-full ' + (currentPlan === 'free' ? 'bg-slate-200 text-slate-700 hover:bg-slate-200' : '')} variant="outline" disabled data-testid="subscribe-free-btn">
              {currentPlan === 'free' ? t('subscriptions.currentPlan') : t('subscriptions.free')}
            </Button>
          </CardContent>
        </Card>

        {/* Basic Plan */}
        <Card className={'border-0 shadow-sm relative ring-2 ' + (currentPlan === 'basic' ? 'ring-cyan-500' : 'ring-cyan-300')} data-testid="plan-basic">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-cyan-500 text-white">{currentPlan === 'basic' ? t('subscriptions.current') : t('subscriptions.mostPopular')}</Badge>
          </div>
          <CardHeader className="text-center pb-4">
            <div className="w-12 h-12 mx-auto bg-cyan-100 dark:bg-cyan-900 rounded-xl flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-cyan-500" />
            </div>
            <CardTitle className="text-xl font-heading">{t('subscriptions.basic')}</CardTitle>
            <CardDescription>{t('subscriptions.basicDescription')}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold">€4,99</span>
              <span className="text-muted-foreground">/{t('subscriptions.month')}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3">
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">20 {i18n.language === 'en' ? 'time entries/month' : 'urenregistraties/maand'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">20 {i18n.language === 'en' ? 'mileage entries/month' : 'km registraties/maand'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">3 {i18n.language === 'en' ? 'clients' : 'klanten'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">4 {i18n.language === 'en' ? 'invoices/month' : 'facturen/maand'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">2 {i18n.language === 'en' ? 'projects' : 'projecten'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">PDF + CSV {i18n.language === 'en' ? 'export' : 'export'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'Email sending' : 'E-mail versturen'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'Dark mode' : 'Donkere modus'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">2 {i18n.language === 'en' ? 'templates' : 'templates'}</span></li>
              <li className="flex items-start gap-2 text-muted-foreground"><span className="w-5 h-5 flex items-center justify-center flex-shrink-0">✗</span><span className="text-sm">{i18n.language === 'en' ? 'No client portal' : 'Geen klantportaal'}</span></li>
              <li className="flex items-start gap-2 text-muted-foreground"><span className="w-5 h-5 flex items-center justify-center flex-shrink-0">✗</span><span className="text-sm">{i18n.language === 'en' ? 'No credit notes' : 'Geen creditnota\'s'}</span></li>
            </ul>
            <Button 
              className={'w-full ' + (currentPlan === 'basic' ? 'bg-slate-200 text-slate-700 hover:bg-slate-200' : 'bg-cyan-500 hover:bg-cyan-600')} 
              disabled={currentPlan === 'basic' || currentPlan === 'pro' || loading}
              onClick={function() { handleSubscribe('basic'); }}
              data-testid="subscribe-basic-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {currentPlan === 'basic' ? t('subscriptions.currentPlan') : currentPlan === 'pro' ? t('subscriptions.downgradeNotPossible') : t('subscriptions.upgrade')}
            </Button>
          </CardContent>
        </Card>

        {/* Pro Plan */}
        <Card className={'border-0 shadow-sm relative ' + (currentPlan === 'pro' ? 'ring-2 ring-cyan-500' : '')} data-testid="plan-pro">
          {currentPlan === 'pro' && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-cyan-500 text-white">{t('subscriptions.current')}</Badge>
            </div>
          )}
          <CardHeader className="text-center pb-4">
            <div className="w-12 h-12 mx-auto bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mb-4">
              <Crown className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <CardTitle className="text-xl font-heading">{t('subscriptions.pro')}</CardTitle>
            <CardDescription>{t('subscriptions.proDescription')}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold">€9,99</span>
              <span className="text-muted-foreground">/{t('subscriptions.month')}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3">
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm font-medium">{i18n.language === 'en' ? 'Unlimited time entries' : 'Onbeperkt urenregistraties'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm font-medium">{i18n.language === 'en' ? 'Unlimited mileage entries' : 'Onbeperkt km registraties'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm font-medium">{i18n.language === 'en' ? 'Unlimited clients' : 'Onbeperkt klanten'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm font-medium">{i18n.language === 'en' ? 'Unlimited invoices' : 'Onbeperkt facturen'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm font-medium">{i18n.language === 'en' ? 'Unlimited projects' : 'Onbeperkt projecten'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'All exports' : 'Alle exports'} (PDF, CSV, Excel, BTW)</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'All 3 invoice templates' : 'Alle 3 factuur templates'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'Client portal' : 'Klantportaal'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'Credit notes' : 'Creditnota\'s'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'Auto reminders' : 'Automatische herinneringen'}</span></li>
              <li className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-sm">{i18n.language === 'en' ? 'Dark mode' : 'Donkere modus'}</span></li>
            </ul>
            <Button 
              className={'w-full ' + (currentPlan === 'pro' ? 'bg-slate-200 text-slate-700 hover:bg-slate-200' : 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600')} 
              disabled={currentPlan === 'pro' || loading}
              onClick={function() { handleSubscribe('pro'); }}
              data-testid="subscribe-pro-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {currentPlan === 'pro' ? t('subscriptions.currentPlan') : t('subscriptions.upgrade')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="text-center text-sm text-muted-foreground mt-8">
        <p>{t('subscriptions.priceNote')}</p>
      </div>
    </div>
  );
}
